import { ListFilterService, useListContext, VisibilityState } from 'ui-builder';
import { PlanTask, TaskType } from 'nekst-api';
import { PlanTasksFilter } from '../types';

export const TYPE_FILTER_NAME = 'TYPE';

export type PlanTasksTypeFilterValue = {
  showStandardTasks: boolean,
  showEmailTasks: boolean,
  showSmsTasks: boolean,
  showEventTasks: boolean,
  showKeyDatesOnly: boolean,
  showPrivateOnly: boolean,
  showBusinessDaysOnly: boolean,
  showNonAiTasksOnly: boolean,
  showRoadmapOnly: boolean,
}

export function usePlanTasksTypeFilterService(): ListFilterService<PlanTasksTypeFilterValue, PlanTask> {
  const listContext = useListContext<PlanTask, PlanTasksFilter>();

  const defaultValue = {
    showStandardTasks: true,
    showEmailTasks: true,
    showSmsTasks: true,
    showEventTasks: true,
    showKeyDatesOnly: false,
    showPrivateOnly: false,
    showNonAiTasksOnly: false,
    showBusinessDaysOnly: false,
    showRoadmapOnly: false,
  } as PlanTasksTypeFilterValue;

  const isInitialized = () => {
    return listContext.filterValues?.showStandardTasks !== undefined
      || listContext.filterValues?.showEmailTasks !== undefined
      || listContext.filterValues?.showSmsTasks !== undefined
      || listContext.filterValues?.showEventTasks !== undefined
      || listContext.filterValues?.showPrivateOnly !== undefined
      || listContext.filterValues?.showBusinessDaysOnly !== undefined
      || listContext.filterValues?.showKeyDatesOnly !== undefined
      || listContext.filterValues?.showNonAiTasksOnly !== undefined
      || listContext.filterValues?.showRoadmapOnly !== undefined;
  };

  const getFilterValue = () => {
    if (isInitialized()) {
      return {
        showStandardTasks: listContext.filterValues?.showStandardTasks ?? defaultValue.showStandardTasks,
        showEmailTasks: listContext.filterValues?.showEmailTasks ?? defaultValue.showEmailTasks,
        showSmsTasks: listContext.filterValues?.showSmsTasks ?? defaultValue.showSmsTasks,
        showEventTasks: listContext.filterValues?.showEventTasks ?? defaultValue.showEventTasks,
        showPrivateOnly: listContext.filterValues?.showPrivateOnly ?? defaultValue.showPrivateOnly,
        showBusinessDaysOnly: listContext.filterValues?.showBusinessDaysOnly ?? defaultValue.showBusinessDaysOnly,
        showKeyDatesOnly: listContext.filterValues?.showKeyDatesOnly ?? defaultValue.showKeyDatesOnly,
        showNonAiTasksOnly: listContext.filterValues?.showNonAiTasksOnly ?? defaultValue.showNonAiTasksOnly,
        showRoadmapOnly: listContext.filterValues?.showRoadmapOnly ?? defaultValue.showRoadmapOnly,
      } as PlanTasksTypeFilterValue;
    } else {
      return defaultValue;
    }
  };

  const setFilterValue = (value: PlanTasksTypeFilterValue) => {
    listContext.setFilterValues!(value);
  };

  const applyFilter = (data: PlanTask) => {
    let result = VisibilityState.HIDDEN;

    const filterValue = getFilterValue();

    if (filterValue.showStandardTasks && data.type === TaskType.STANDARD) {
      result = VisibilityState.VISIBLE;
    } else if (filterValue.showEmailTasks && data.type === TaskType.EMAIL) {
      result = VisibilityState.VISIBLE;
    } else if (filterValue.showSmsTasks && data.type === TaskType.SMS) {
      result = VisibilityState.VISIBLE;
    } else if (filterValue.showEventTasks && data.type === TaskType.EVENT) {
      result = VisibilityState.VISIBLE;
    }

    if (filterValue.showKeyDatesOnly && !data.isKeyDate) {
      result = VisibilityState.HIDDEN;
    }

    if (filterValue.showPrivateOnly && !data.isPrivate) {
      result = VisibilityState.HIDDEN;
    }

    if (filterValue.showNonAiTasksOnly && data.isAiTask) {
      result = VisibilityState.HIDDEN;
    }

    if (filterValue.showBusinessDaysOnly && !data.useBusinessDaysCalculation) {
      result = VisibilityState.HIDDEN;
    }

    if (filterValue.showRoadmapOnly && !data.isIncludedToRoadmap) {
      result = VisibilityState.HIDDEN;
    }

    return result;
  };

  return {
    getName: () => TYPE_FILTER_NAME,
    getDefaultValue: () => defaultValue,
    getFilterValue,
    setFilterValue,
    applyFilter,
  };
}
